import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Remove as NeutralIcon,
  ShieldOutlined,
  WarningAmberOutlined,
  AccessTime,
} from '@mui/icons-material';

const STRATEGY_LABELS = {
  long_call: 'Long Call',
  long_put: 'Long Put',
  covered_call: 'Covered Call',
  cash_secured_put: 'Cash-Secured Put',
  protective_put: 'Protective Put',
  bull_call_debit_spread: 'Bull Call Debit Spread',
  bear_put_debit_spread: 'Bear Put Debit Spread',
};

const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v === null || v === undefined ? 'N/A' : `${(Number(v) * 100).toFixed(1)}%`);

const outlookIcon = (outlook) => {
  if (outlook === 'bullish') return <TrendingUp fontSize="small" color="success" />;
  if (outlook === 'bearish') return <TrendingDown fontSize="small" color="error" />;
  return <NeutralIcon fontSize="small" color="disabled" />;
};

const OptionStrategyCard = ({ rec }) => {
  const label = STRATEGY_LABELS[rec.strategy_type] || rec.strategy_type;
  const netCost = Number(rec.net_cost);
  const isCredit = netCost < 0;
  const mgmt = rec.management_guidance || rec.management || {};
  const breakevens = Array.isArray(rec.breakevens) ? rec.breakevens : [];
  const legs = rec.legs || [];
  const needsShares = rec.needs_shares;
  const sharesRequired = rec.shares_required || 0;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {outlookIcon(rec.directional_outlook || rec.outlook)}
            <Typography variant="h6">{label}</Typography>
          </Box>
          <Chip
            label={`${isCredit ? 'Credit' : 'Debit'} ${money(Math.abs(netCost))}`}
            color={isCredit ? 'success' : 'default'}
            size="small"
          />
        </Box>

        {/* Rationale */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {rec.rationale}
        </Typography>

        {sharesRequired > 0 && (
          <Chip
            sx={{ mt: 1 }}
            size="small"
            color={needsShares ? 'warning' : 'success'}
            label={
              needsShares
                ? `Requires ${sharesRequired} shares — you must buy shares first`
                : `Requires ${sharesRequired} shares — you hold enough`
            }
          />
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Legs */}
        <Typography variant="subtitle2" gutterBottom>Legs</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Side</TableCell>
              <TableCell>Instrument</TableCell>
              <TableCell>Strike</TableCell>
              <TableCell>Expiration</TableCell>
              <TableCell align="right">Qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {legs.map((leg, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Chip size="small" label={leg.leg_side || leg.side} color={(leg.leg_side || leg.side) === 'buy' ? 'primary' : 'secondary'} />
                </TableCell>
                <TableCell>
                  {leg.instrument === 'shares' ? 'Shares' : `${leg.contract_type || ''}`.toUpperCase()}
                </TableCell>
                <TableCell>{leg.strike || leg.strike_price ? money(leg.strike || leg.strike_price) : '—'}</TableCell>
                <TableCell>{leg.expiration || leg.expiration_date || '—'}</TableCell>
                <TableCell align="right">{leg.quantity}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 1.5 }} />

        {/* Risk / reward */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1 }}>
          <Metric label="Max Profit" value={rec.max_profit_unbounded ? 'Unbounded' : money(rec.max_profit)} />
          <Metric label="Max Loss" value={money(rec.max_loss)} />
          <Metric label="Breakeven" value={breakevens.length ? breakevens.map(money).join(', ') : '—'} />
          <Metric label="Prob. of Profit" value={pct(rec.probability_of_profit)} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 1, mt: 1 }}>
          <Metric label="Delta" value={fmtGreek(rec.position_delta ?? rec.position_greeks?.delta)} />
          <Metric label="Gamma" value={fmtGreek(rec.position_gamma ?? rec.position_greeks?.gamma)} />
          <Metric label="Theta" value={fmtGreek(rec.position_theta ?? rec.position_greeks?.theta)} />
          <Metric label="Vega" value={fmtGreek(rec.position_vega ?? rec.position_greeks?.vega)} />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Position management guidance */}
        <Typography variant="subtitle2" gutterBottom>Position Management</Typography>

        {mgmt.cutoff && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <AccessTime fontSize="small" color="action" />
            <Typography variant="body2">
              Cutoff: {mgmt.cutoff.expiration} ({mgmt.cutoff.days_to_expiry} days).{' '}
              {mgmt.time_decay_note}
            </Typography>
          </Box>
        )}
        {mgmt.max_loss_scenario && (
          <GuidanceLine icon={<WarningAmberOutlined fontSize="small" color="error" />} text={mgmt.max_loss_scenario} />
        )}
        {mgmt.max_profit_scenario && (
          <GuidanceLine icon={<TrendingUp fontSize="small" color="success" />} text={mgmt.max_profit_scenario} />
        )}
        {mgmt.loss_scenario && (
          <GuidanceLine icon={<TrendingDown fontSize="small" color="warning" />} text={mgmt.loss_scenario} />
        )}
        {mgmt.assignment_note && (
          <GuidanceLine icon={<ShieldOutlined fontSize="small" color="info" />} text={mgmt.assignment_note} />
        )}

        {Array.isArray(mgmt.actions) && mgmt.actions.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Management actions</Typography>
            {mgmt.actions.map((a, i) => (
              <GuidanceLine
                key={i}
                icon={
                  a.purpose === 'protect_profit'
                    ? <ShieldOutlined fontSize="small" color="success" />
                    : <WarningAmberOutlined fontSize="small" color="warning" />
                }
                text={`${a.type.replace(/_/g, ' ')}: ${a.trigger}`}
              />
            ))}
          </Box>
        )}

        {mgmt.recommended_exit && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }} color="text.secondary">
            Recommended exit: {mgmt.recommended_exit}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const Metric = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" fontWeight={600}>{value}</Typography>
  </Box>
);

const GuidanceLine = ({ icon, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
    <Box sx={{ mt: '2px' }}>{icon}</Box>
    <Typography variant="body2">{text}</Typography>
  </Box>
);

const fmtGreek = (v) => (v === null || v === undefined ? 'N/A' : Number(v).toFixed(2));

export default OptionStrategyCard;
